import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

const SpaceConstellationVisual = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = canvas.width = canvas.offsetWidth;
    let h = canvas.height = canvas.offsetHeight;

    const particles = [];
    const particleCount = 40;

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        r: Math.random() * 2 + 1,
      });
    }

    let mx = -1000;
    let my = -1000;

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mx = e.clientX - rect.left;
      my = e.clientY - rect.top;
    };

    const handleMouseLeave = () => {
      mx = -1000;
      my = -1000;
    };

    const resize = () => {
      if (canvas) {
        w = canvas.width = canvas.offsetWidth;
        h = canvas.height = canvas.offsetHeight;
      }
    };

    window.addEventListener('resize', resize);
    
    const parent = canvas.parentElement;
    if (parent) {
      parent.addEventListener('mousemove', handleMouseMove);
      parent.addEventListener('mouseleave', handleMouseLeave);
    }

    let frameId;

    const draw = () => {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, w, h);

      for (let i = 0; i < particleCount; i++) {
        const p1 = particles[i];
        
        p1.x += p1.vx;
        p1.y += p1.vy;

        if (p1.x < 0 || p1.x > w) p1.vx *= -1;
        if (p1.y < 0 || p1.y > h) p1.vy *= -1;

        ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.beginPath();
        ctx.arc(p1.x, p1.y, p1.r, 0, Math.PI * 2);
        ctx.fill();

        for (let j = i + 1; j < particleCount; j++) {
          const p2 = particles[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 90) {
            ctx.strokeStyle = `rgba(20, 184, 166, ${0.12 * (1 - dist / 90)})`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }

        if (mx !== -1000) {
          const dx = p1.x - mx;
          const dy = p1.y - my;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.strokeStyle = `rgba(99, 102, 241, ${0.25 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.9;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(mx, my);
            ctx.stroke();
          }
        }
      }

      frameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', resize);
      if (parent) {
        parent.removeEventListener('mousemove', handleMouseMove);
        parent.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1,
        opacity: 0.6,
      }}
    />
  );
};

const FloatingAstronaut = () => (
  <motion.div
    animate={{
      y: [0, -18, 0],
      rotate: [0, 3, -3, 0],
    }}
    transition={{
      repeat: Infinity,
      duration: 8,
      ease: "easeInOut",
    }}
    style={{
      width: '240px',
      height: '240px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2,
      pointerEvents: 'none',
      filter: 'drop-shadow(0 15px 30px rgba(20, 184, 166, 0.15))',
    }}
  >
    <svg viewBox="0 0 200 200" fill="none" style={{ width: '100%', height: '100%' }}>
      <circle cx="100" cy="95" r="42" fill="rgba(255, 255, 255, 0.02)" stroke="rgba(255, 255, 255, 0.15)" strokeWidth="2.5" />
      <path d="M 68 85 A 32 32 0 0 1 132 85 A 32 24 0 0 1 68 85 Z" fill="rgba(20, 184, 166, 0.08)" stroke="rgba(20, 184, 166, 0.35)" strokeWidth="1.8" />
      <path d="M 74 76 Q 100 68 126 76" stroke="rgba(255, 255, 255, 0.4)" strokeWidth="1.5" strokeLinecap="round" />
      <rect x="80" y="112" width="40" height="24" rx="6" fill="rgba(255, 255, 255, 0.04)" stroke="rgba(255, 255, 255, 0.15)" strokeWidth="1.8" />
      <circle cx="90" cy="124" r="2.5" fill="#14b8a6" />
      <circle cx="100" cy="124" r="2.5" fill="#ec4899" />
      <rect x="107" y="122" width="8" height="3" rx="1" fill="rgba(255, 255, 255, 0.2)" />
      <path d="M 52 104 Q 40 120 48 132" stroke="rgba(255, 255, 255, 0.12)" strokeWidth="3" strokeLinecap="round" />
      <path d="M 148 104 Q 160 120 152 132" stroke="rgba(255, 255, 255, 0.12)" strokeWidth="3" strokeLinecap="round" />
      <path d="M 90 136 Q 80 155 100 162" stroke="rgba(255, 255, 255, 0.06)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
      <circle cx="45" cy="40" r="1.5" fill="#fff" opacity="0.6">
        <animate attributeName="opacity" values="0.6;0.2;0.6" dur="2.2s" repeatCount="indefinite" />
      </circle>
      <circle cx="155" cy="55" r="1" fill="#fff" opacity="0.4">
        <animate attributeName="opacity" values="0.4;0.8;0.4" dur="1.7s" repeatCount="indefinite" />
      </circle>
    </svg>
  </motion.div>
);

const CTA = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 5000);
  };

  return (
    <section className="cta-section" id="cta" ref={ref} style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Constellation Canvas background */}
      <SpaceConstellationVisual />

      {/* Dynamic Nebulae Orbs */}
      <div style={{
        position: 'absolute', top: '15%', left: '10%',
        width: '320px', height: '320px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(20, 184, 166, 0.08), transparent 70%)',
        filter: 'blur(40px)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />
      <div style={{
        position: 'absolute', bottom: '10%', right: '10%',
        width: '380px', height: '380px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.08), transparent 70%)',
        filter: 'blur(50px)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        >
          <p style={{
            fontSize: '11px', fontWeight: 700, letterSpacing: '0.22em',
            textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)',
            marginBottom: '16px',
          }}>
            Reach Out
          </p>
          <h2 className="cta-title" style={{ marginBottom: '40px' }}>
            Let's build something<br />
            <span style={{
              background: 'linear-gradient(180deg, #fff 0%, rgba(255,255,255,0.4) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              out of this world.
            </span>
          </h2>

          {/* Grid Layout: Left visual columns, Right form column */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.2fr',
            gap: '40px',
            textAlign: 'left',
            alignItems: 'center',
          }} className="cta-grid">
            <style>{`
              @media (max-width: 860px) {
                .cta-grid {
                  grid-template-columns: 1fr !important;
                  gap: 30px !important;
                }
                .astro-column {
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  text-align: center;
                }
              }
            `}</style>

            {/* Left Column: Floating Astronaut and links */}
            <div className="astro-column" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <FloatingAstronaut />
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <a href="mailto:hello@janmengineering.com" style={{
                  display: 'flex', alignItems: 'center', gap: '14px',
                  background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.05)',
                  borderRadius: '16px', padding: '14px 20px', backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
                  color: 'var(--ink-2)', fontSize: '14px',
                  transition: 'border-color 0.3s',
                }}
                onMouseOver={(e) => e.currentTarget.style.borderColor = 'rgba(20, 184, 166, 0.4)'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.05)'}>
                  <span style={{ fontSize: '18px' }}>✉</span>
                  hello@janmengineering.com
                </a>
                <a href="https://wa.me/91XXXXXXXXXX" style={{
                  display: 'flex', alignItems: 'center', gap: '14px',
                  background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.05)',
                  borderRadius: '16px', padding: '14px 20px', backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
                  color: 'var(--ink-2)', fontSize: '14px',
                  transition: 'border-color 0.3s',
                }}
                onMouseOver={(e) => e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.4)'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.05)'}>
                  <span style={{ fontSize: '18px' }}>◎</span>
                  Message us on WhatsApp
                </a>
              </div>
            </div>

            {/* Right Column: Glassmorphic Contact Form */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.015)',
              backdropFilter: 'blur(24px) saturate(180%)',
              WebkitBackdropFilter: 'blur(24px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.06)',
              borderRadius: '24px',
              padding: '36px',
              boxShadow: '0 24px 64px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
              position: 'relative',
            }}>
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }} className="form-row">
                  <style>{`
                    @media (max-width: 480px) {
                      .form-row { grid-template-columns: 1fr !important; gap: 16px !important; }
                    }
                  `}</style>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Full Name</label>
                    <input type="text" placeholder="John Doe" required style={{
                      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
                      borderRadius: '12px', padding: '12px 16px', color: 'var(--ink)', outline: 'none',
                      fontSize: '14px', transition: 'all 0.3s',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#14b8a6'; e.target.style.background = 'rgba(255,255,255,0.05)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.07)'; e.target.style.background = 'rgba(255,255,255,0.03)'; }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email Address</label>
                    <input type="email" placeholder="john@company.com" required style={{
                      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
                      borderRadius: '12px', padding: '12px 16px', color: 'var(--ink)', outline: 'none',
                      fontSize: '14px', transition: 'all 0.3s',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#14b8a6'; e.target.style.background = 'rgba(255,255,255,0.05)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.07)'; e.target.style.background = 'rgba(255,255,255,0.03)'; }} />
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Required Service</label>
                  <input type="text" placeholder="e.g. MasseShots Booking System, AI Systems, custom API..." required style={{
                    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: '12px', padding: '12px 16px', color: 'var(--ink)', outline: 'none',
                    fontSize: '14px', transition: 'all 0.3s',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#14b8a6'; e.target.style.background = 'rgba(255,255,255,0.05)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.07)'; e.target.style.background = 'rgba(255,255,255,0.03)'; }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Project details</label>
                  <textarea placeholder="Describe your platform concept, timelines, and scale..." required rows="4" style={{
                    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: '12px', padding: '12px 16px', color: 'var(--ink)', outline: 'none',
                    fontSize: '14px', transition: 'all 0.3s', resize: 'vertical', fontFamily: 'inherit',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#14b8a6'; e.target.style.background = 'rgba(255,255,255,0.05)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.07)'; e.target.style.background = 'rgba(255,255,255,0.03)'; }} />
                </div>

                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    background: 'linear-gradient(90deg, #14b8a6, #6366f1)',
                    color: '#ffffff',
                    fontWeight: 700,
                    border: 'none',
                    borderRadius: '12px',
                    padding: '14px',
                    fontSize: '15px',
                    cursor: 'pointer',
                    boxShadow: '0 8px 24px rgba(20, 184, 166, 0.25)',
                    marginTop: '8px',
                  }}
                >
                  Launch Transmission
                </motion.button>
              </form>

              {sent && (
                <div style={{
                  position: 'absolute', inset: 0, background: 'rgba(10,10,12,0.95)',
                  borderRadius: '24px', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: '10px',
                  zIndex: 10, backdropFilter: 'blur(8px)',
                }}>
                  <span style={{ fontSize: '40px', color: '#14b8a6' }}>✓</span>
                  <div style={{ fontSize: '18px', fontWeight: 700 }}>Signal Sent Successfully!</div>
                  <div style={{ fontSize: '13px', color: 'var(--ink-3)' }}>We'll connect back with you within 24 hours.</div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTA;
